import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router';
import useMeasure from 'react-use-measure';

import { SwingThread } from '@data';
import { useSwingThreads } from '@queries';
import {
    activeSegmentationsMapAtom,
    activeSwingIdAtom,
    activeUserIdAtom,
    progressAtom,
    selectedPositionAtom,
    swingThreadOpenAtom,
    swingThreadToggleAtom,
    totalFramesAtom,
} from '@store';
import { Box } from '@ui';

import { ScrubberMarks } from '../ScrubberMarks/ScrubberMarks';
import { ThinPlaybackControls } from '../ThinPlaybackControls';

import { PlaybackControls } from './PlaybackControls';
import { PositionControls } from './PositionControls';
import { PositionMarkerList } from './PositionMarker';
import { ProgressBar } from './ProgressBar';
import * as css from './Scrubber.css';
import { useIsSwingDataLoading } from './useIsSwingDataLoading';
import { WithAutoplay } from './WithAutoplay';

const IMPACT_POSITION = 7;

const calculateFrameRatios = (
    segmentation: number[],
    totalFrames: number | null | undefined,
    offset = 0,
): { [key: string]: number } => {
    if (!totalFrames) return {};

    const acc: Record<string, number> = {};

    segmentation.forEach((value, index) => {
        if (value === null) {
            acc[index] = -1;
        } else {
            const ratio = (value + offset) / totalFrames;
            // Clamp ratio between 0 and 1
            acc[index] = Math.min(Math.max(ratio, 0), 1);
        }
    });

    return acc;
};
const SwingThreadMarkersContainer = () => {
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const { data: swingThreads } = useSwingThreads(activeSwingId);

    const [searchParams] = useSearchParams();
    const [swingThreadToggle, setThreadToggle] = useAtom(swingThreadToggleAtom);

    const threadId = useMemo(() => {
        const raw = searchParams.get('threadId');
        return raw ? Number(raw) : null;
    }, [searchParams]);

    const prevIdSignature = useRef<string>('');
    const currentIdSignature = useMemo(
        () =>
            swingThreads
                ?.map((d) => d.id)
                .sort()
                .join(',') ?? '',
        [swingThreads],
    );
    const threadsChanged = prevIdSignature.current !== currentIdSignature;

    useEffect(() => {
        prevIdSignature.current = currentIdSignature;
    }, [currentIdSignature]);

    const prevNotificationRef = useRef<number | null>(null);
    const notificationChanged = prevNotificationRef.current !== threadId;

    useEffect(() => {
        prevNotificationRef.current = threadId;
    }, [threadId]);

    const prevMapRef = useRef<Record<number, boolean>>({});
    // if this recomputes it rerenders the swing thread markers making it impossible to write a comment or delete a thread
    const swingThreadMap = useMemo(() => {
        if (!swingThreads) return prevMapRef.current;

        const shouldRecompute = threadsChanged || notificationChanged;

        if (!shouldRecompute) {
            return prevMapRef.current;
        }

        const next = Object.fromEntries(swingThreads.map((t) => [t.id, t.id === threadId])) as Record<number, boolean>;

        prevMapRef.current = next;
        return next;
    }, [swingThreads, threadId, threadsChanged, notificationChanged]);

    useEffect(() => {
        if (threadId) setThreadToggle(true);
    }, [threadId, setThreadToggle]);

    const isReady = !!(swingThreads && activeSwingId && swingThreadToggle && Object.keys(swingThreadMap).length);

    return (
        isReady && (
            <SwingThreadMarkersView
                activeSwingId={activeSwingId}
                swingThreads={swingThreads}
                notificationThreadId={threadId}
                swingThreadMap={swingThreadMap}
            />
        )
    );
};

type SwingThreadMarkersViewProps = {
    swingThreads: SwingThread[];
    activeSwingId: string;
    notificationThreadId: number | null;
    swingThreadMap: Record<string, boolean>;
};

type LastLinkCondition = { totalFrames: number; threadId: number };

const SwingThreadMarkersView = ({
    swingThreads,
    activeSwingId,
    notificationThreadId,
    swingThreadMap,
}: SwingThreadMarkersViewProps) => {
    const [ref, { width }] = useMeasure({ debounce: 150 });

    const activeUserId = useAtomValue(activeUserIdAtom);

    const [openThreads, setOpenThreads] = useAtom(swingThreadOpenAtom);
    // temporary solution until we have a solid atom strategy, these atoms are currently fragmented.
    // you could have total frames for swing B and segmentations for swing A in one render cycle causing rerenders and component skipping
    // The waiting on the load in the parent component helps but does not fix it fully, we move the scrubber until we get the last unique
    // pairing of threadid and total frames
    const lastLinkCondition = useRef<LastLinkCondition | null>(null);
    const setProgress = useSetAtom(progressAtom);
    const totalFrames = useAtomValue(totalFramesAtom);

    const segmentation = useAtomValue(activeSegmentationsMapAtom);
    const segmentationRatios = useMemo(
        () => calculateFrameRatios(Object.values(segmentation || {}), totalFrames),
        [totalFrames, segmentation],
    );
    useEffect(() => {
        setOpenThreads(swingThreadMap);
    }, [setOpenThreads, swingThreadMap]);

    useEffect(() => {
        if (
            notificationThreadId &&
            (notificationThreadId !== lastLinkCondition.current?.threadId ||
                totalFrames !== lastLinkCondition.current?.totalFrames)
        ) {
            const frame = swingThreads.find((d) => d.id === notificationThreadId)?.fromFrame ?? 1;
            const progress = (frame / totalFrames) * 100;
            setProgress(progress);
            lastLinkCondition.current = { threadId: notificationThreadId, totalFrames };
        }
    }, [notificationThreadId, activeSwingId, setProgress, swingThreads, totalFrames]);

    const frameMarkerPositions = useMemo(() => {
        return Object.entries(segmentationRatios)
            .filter(([, val]) => val !== -1)
            .map(([key, ratio]) => ({
                position: Number(key) + 1,
                ratio,
                isImpact: Number(key) + 1 === IMPACT_POSITION,
            }));
    }, [segmentationRatios]);

    return (
        <div className={css.scrubberContentGhost} ref={ref}>
            <ScrubberMarks
                activeUserId={activeUserId ?? ''}
                markers={swingThreads}
                swingUuid={activeSwingId}
                totalFrames={totalFrames}
                pPositions={frameMarkerPositions}
                width={width}
                openThreads={openThreads}
                setOpenThreads={setOpenThreads}
            />
        </div>
    );
};

export const Scrubber = () => {
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const { swingAnalysis, swingVideos } = useIsSwingDataLoading(activeSwingId ?? '');
    // Temporary until we settle on a atom strategy, as is these atoms could be returning data for different swings
    // causing rerenders and marker shifts
    const isLoading = swingAnalysis.isLoading || swingVideos.isLoading;
    return (
        <div className={css.fixedScrubber}>
            <div className={css.scrubberWrapper}>
                {!isLoading ? <SwingThreadMarkersContainer /> : null}
                <ProgressBar>
                    <PositionMarkerList />
                </ProgressBar>
            </div>
            <PositionControls />
        </div>
    );
};

type PlaybackScrubberProps = {
    syncOnly?: boolean;
    freezePosition?: string | null;
    onFreezePositionChange?: (position: string | null) => void;
};

export const PlaybackScrubber = ({ syncOnly }: PlaybackScrubberProps) => {
    return (
        <WithAutoplay>
            <div className={css.blockScrubber}>
                <PlaybackControls syncOnly={syncOnly} />
                <div className={css.scrubberWrapper}>
                    <ProgressBar>
                        <PositionMarkerList />
                    </ProgressBar>
                </div>
            </div>
        </WithAutoplay>
    );
};

const handleThinScrubberPointerDownCapture: React.PointerEventHandler<HTMLDivElement> = (event) => {
    event.currentTarget.focus();
};

export const PlaybackScrubberThin = ({ syncOnly }: PlaybackScrubberProps) => {
    return (
        <WithAutoplay>
            <Box
                className={css.blockScrubber}
                onPointerDownCapture={handleThinScrubberPointerDownCapture}
                tabIndex={-1}
            >
                <ThinPlaybackControls syncOnly={syncOnly}>
                    <Box className={css.thinScrubberWrapper}>
                        <ProgressBar variant="thin">
                            <PositionMarkerList />
                        </ProgressBar>
                    </Box>
                </ThinPlaybackControls>
            </Box>
        </WithAutoplay>
    );
};

export const Playback3DScrubber = ({ syncOnly, freezePosition, onFreezePositionChange }: PlaybackScrubberProps) => {
    const setSelectedPosition = useSetAtom(selectedPositionAtom);

    const handleScrubStart = useCallback(() => {
        setSelectedPosition(null);
    }, [setSelectedPosition]);

    return (
        <WithAutoplay>
            <div className={css.playback3DScrubber}>
                <PlaybackControls
                    syncOnly={syncOnly}
                    freezePosition={freezePosition}
                    onFreezePositionChange={onFreezePositionChange}
                />
                <div className={css.scrubberWrapper}>
                    <ProgressBar onScrubStart={handleScrubStart}>
                        <PositionMarkerList />
                    </ProgressBar>
                </div>
            </div>
        </WithAutoplay>
    );
};
